import type { ModuleRow } from '~/pages/backoffice/admin/modules/ModulesList'
import { Loader, Upload } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import FileDropZone from '~/components/FileDropZone'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import UploadProgress from '~/components/UploadProgress'
import ReplacementImpactSummary from '~/pages/backoffice/admin/modules/ReplacementImpactSummary'
import { useModuleReplacement } from '~/pages/backoffice/admin/modules/use-module-replacement'
import { formatBytes } from '~/utils/bytes'

type Props = {
  module: Pick<ModuleRow, 'id' | 'title'>
  /** Poids maximal d'un package, borne de l'API ; inconnu tant que le quota n'est pas chargé. */
  maxUploadBytes?: number | null
  onClose: () => void
  /** Après la bascule : la liste et le quota doivent suivre. */
  onReplaced: () => Promise<unknown>
}

/**
 * Remplacer le contenu d'un module (#311) : déposer le nouveau .zip, le
 * laisser partir (#310), lire ce que ça change, puis confirmer. Tant que
 * l'admin n'a pas confirmé, les apprenants voient l'ancienne version ; fermer
 * jette le dépôt.
 */
function ModuleReplaceDialog({ module, maxUploadBytes, onClose, onReplaced }: Props) {
  const { t, i18n } = useTranslation()
  const { staged, error, state, cancelUpload, stage, apply, discard, busy } = useModuleReplacement(module.id)
  const [file, setFile] = useState<File | null>(null)

  // Pas de fermeture pendant un envoi ou une bascule : le dépôt arriverait
  // après coup, et une annulation croiserait la confirmation. L'envoi, lui,
  // s'annule par la barre de progression.
  const close = () => {
    if (busy) return
    void discard()
    onClose()
  }

  const handleStage = () => {
    if (!file) {
      toast.error(t('modules.selectFile'))
      return
    }
    void stage(file)
  }

  const handleApply = async () => {
    try {
      await apply()
      toast.success(t('modules.replace.done'))
      onClose()
      await onReplaced()
    } catch {
      toast.error(t('modules.replace.failed'))
    }
  }

  return (
    <Dialog open onOpenChange={next => !next && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('modules.replace.title', { name: module.title })}</DialogTitle>
          <DialogDescription>{t(staged ? 'modules.replace.reviewIntro' : 'modules.replace.intro')}</DialogDescription>
        </DialogHeader>

        {staged
          ? <ReplacementImpactSummary impact={staged.impact} />
          : (
              <div className="flex flex-col gap-3">
                <FileDropZone
                  id="module-replace-file"
                  rule={{ extensions: ['.zip'], maxBytes: maxUploadBytes }}
                  file={file}
                  onFile={setFile}
                  disabled={busy}
                  hint={maxUploadBytes
                    ? t('modules.upload.hint', { max: formatBytes(maxUploadBytes, i18n.language) })
                    : t('modules.upload.hintNoMax')}
                />
                <UploadProgress state={state} processingLabel={t('modules.upload.processing')} onCancel={cancelUpload} />
                {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
              </div>
            )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>{t('common.cancel')}</Button>
          {staged
            ? (
                <Button onClick={handleApply} disabled={busy}>
                  {busy && <Loader className="mr-2 size-4 animate-spin" />}
                  {t('modules.replace.confirm')}
                </Button>
              )
            : (
                <Button onClick={handleStage} disabled={busy}>
                  {busy ? <Loader className="mr-2 size-4 animate-spin" /> : <Upload className="mr-2 size-4" />}
                  {t('modules.replace.upload')}
                </Button>
              )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ModuleReplaceDialog
